import { useState, useEffect } from 'react';
import { Sport } from '../types';
import { BOOKMAKERS_BY_REGION, REGION_LABELS } from '../bookmakers';
import BookmakerLogo from './BookmakerLogo';
import { PLAYER_PROP_LEAGUES, MAX_PLAYER_PROP_MARKETS } from '../playerPropMarkets';

import { getSavedFiltersByType, saveFilter, deleteFilter, type SavedFilter, type ArbitrageFilters, type EVFilters } from '../utils/savedFilters';

interface FiltersPanelProps {
  isOpen: boolean;
  onCancel: () => void;
  onApply: () => void;
  sports: Sport[];
  selectedSports: string[];
  setSelectedSports: (sports: string[]) => void;
  selectedRegions: string[];
  setSelectedRegions: (regions: string[]) => void;
  selectedMarkets: string[];
  setSelectedMarkets: (markets: string[]) => void;
  selectedBookmakers: string[];
  setSelectedBookmakers: (bookmakers: string[]) => void;
  selectedGameStatus: string[];
  setSelectedGameStatus: (status: string[]) => void;
  selectedPlayerPropMarkets?: string[];
  setSelectedPlayerPropMarkets?: (markets: string[]) => void;
  minProfit: number;
  setMinProfit: (profit: number) => void;
  maxProfit: number;
  setMaxProfit: (profit: number) => void;
  minProbability?: number;
  setMinProbability?: (prob: number) => void;
  maxProbability?: number;
  setMaxProbability?: (prob: number) => void;
  isEVMode?: boolean;
  userTier?: 'free' | 'gold' | 'platinum';
}

type Category = 'saved' | 'sportsbook' | 'percentage' | 'sport' | 'market' | 'date_range';

const MARKETS = [
  { key: 'h2h', name: 'Moneyline (H2H)' },
  { key: 'spreads', name: 'Point Spreads' },
  { key: 'totals', name: 'Totals (Over/Under)' },
];

export default function FiltersPanel({
  isOpen,
  onCancel,
  onApply,
  sports,
  selectedSports,
  setSelectedSports,
  selectedRegions,
  setSelectedRegions: _setSelectedRegions,
  selectedMarkets,
  setSelectedMarkets,
  selectedBookmakers,
  setSelectedBookmakers,
  selectedGameStatus,
  setSelectedGameStatus,
  selectedPlayerPropMarkets = [],
  setSelectedPlayerPropMarkets = () => {},
  minProfit,
  setMinProfit,
  maxProfit,
  setMaxProfit,
  minProbability = 0,
  setMinProbability = () => { },
  maxProbability = 100,
  setMaxProbability = () => { },
  isEVMode = false,
  userTier = 'free',
}: FiltersPanelProps) {
  const [activeCategory, setActiveCategory] = useState<Category>('saved');
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [expandedLeagues, setExpandedLeagues] = useState<string[]>([]);

  // Region selection UI is temporarily disabled but the setter remains for future use
  void _setSelectedRegions;

  // Load saved filters when panel opens
  useEffect(() => {
    if (isOpen) {
      const filters = getSavedFiltersByType(isEVMode ? 'ev' : 'arbitrage');
      setSavedFilters(filters);
    }
  }, [isOpen, isEVMode]);



  const categories: Array<{ key: Category; label: string; icon: string }> = [
    { key: 'saved', label: 'Saved', icon: '⭐' },
    { key: 'sportsbook', label: 'Sportsbook', icon: '🎰' },
    { key: 'percentage', label: 'Percentage', icon: '%' },
    { key: 'sport', label: 'Sport', icon: '⚽' },
    { key: 'market', label: 'Market', icon: '📊' },
    { key: 'date_range', label: 'Date Range', icon: '📅' },
  ];

  const handleSportToggle = (sportKey: string) => {
    if (sportKey === 'upcoming') {
      setSelectedSports(['upcoming']);
      return;
    }

    const newSelection = selectedSports.filter(s => s !== 'upcoming');

    if (newSelection.includes(sportKey)) {
      const filtered = newSelection.filter(s => s !== sportKey);
      setSelectedSports(filtered.length > 0 ? filtered : ['upcoming']);
    } else {
      setSelectedSports([...newSelection, sportKey]);
    }
  };

  const handleMarketToggle = (marketKey: string) => {
    if (selectedMarkets.includes(marketKey)) {
      const filtered = selectedMarkets.filter(m => m !== marketKey);
      // Allow empty game markets only if player props are selected
      if (filtered.length > 0 || selectedPlayerPropMarkets.length > 0) {
        setSelectedMarkets(filtered);
      }
    } else {
      setSelectedMarkets([...selectedMarkets, marketKey]);
    }
  };

  const handleBookmakerToggle = (bookmakerKey: string) => {
    if (selectedBookmakers.includes(bookmakerKey)) {
      setSelectedBookmakers(selectedBookmakers.filter(b => b !== bookmakerKey));
    } else {
      setSelectedBookmakers([...selectedBookmakers, bookmakerKey]);
    }
  };

  const handleSelectAllInRegion = (regionKey: string) => {
    const regionBookmakers = BOOKMAKERS_BY_REGION[regionKey as keyof typeof BOOKMAKERS_BY_REGION];
    const regionKeys = regionBookmakers.map(b => b.key);
    const allSelected = regionKeys.every(key => selectedBookmakers.includes(key));

    if (allSelected) {
      // Deselect all in this region
      setSelectedBookmakers(selectedBookmakers.filter(key => !regionKeys.includes(key)));
    } else {
      // Select all in this region
      const newSelection = [...selectedBookmakers];
      regionKeys.forEach(key => {
        if (!newSelection.includes(key)) {
          newSelection.push(key);
        }
      });
      setSelectedBookmakers(newSelection);
    }
  };

  const handleGameStatusToggle = (status: string) => {
    if (selectedGameStatus.includes(status)) {
      const filtered = selectedGameStatus.filter(s => s !== status);
      setSelectedGameStatus(filtered.length > 0 ? filtered : [status]);
    } else {
      setSelectedGameStatus([...selectedGameStatus, status]);
    }
  };

  // Player prop league/market functions
  const handleLeagueToggle = (leagueKey: string) => {
    setExpandedLeagues(prev =>
      prev.includes(leagueKey)
        ? prev.filter(k => k !== leagueKey)
        : [...prev, leagueKey]
    );
  };

  const handlePlayerPropMarketToggle = (marketKey: string) => {
    if (selectedPlayerPropMarkets.includes(marketKey)) {
      const filtered = selectedPlayerPropMarkets.filter(m => m !== marketKey);
      // Allow empty player props only if game markets are selected
      if (filtered.length > 0 || selectedMarkets.length > 0) {
        setSelectedPlayerPropMarkets(filtered);
      }
    } else if (selectedPlayerPropMarkets.length < MAX_PLAYER_PROP_MARKETS) {
      setSelectedPlayerPropMarkets([...selectedPlayerPropMarkets, marketKey]);
    }
  };

  const handleSelectAllMarketsInLeague = (leagueKey: string) => {
    const league = PLAYER_PROP_LEAGUES.find(l => l.key === leagueKey);
    if (!league) return;

    const standardMarkets = league.markets.filter(m => !m.isAlternate);
    const leagueMarketKeys = standardMarkets.map(m => m.key);
    const currentlySelected = leagueMarketKeys.filter(key => selectedPlayerPropMarkets.includes(key));
    const allSelected = currentlySelected.length === leagueMarketKeys.length;

    if (allSelected) {
      // Deselect all in this league
      const filtered = selectedPlayerPropMarkets.filter(key => !leagueMarketKeys.includes(key));
      // Allow empty player props only if game markets are selected
      if (filtered.length > 0 || selectedMarkets.length > 0) {
        setSelectedPlayerPropMarkets(filtered);
      }
    } else {
      // Select as many as we can (up to the limit)
      const remaining = MAX_PLAYER_PROP_MARKETS - selectedPlayerPropMarkets.length + currentlySelected.length;
      const marketsToAdd = leagueMarketKeys.slice(0, remaining);
      const newSelection = selectedPlayerPropMarkets.filter(key => !leagueMarketKeys.includes(key));
      setSelectedPlayerPropMarkets([...newSelection, ...marketsToAdd]);
    }
  };

  const getSelectedCountForLeague = (leagueKey: string): number => {
    const league = PLAYER_PROP_LEAGUES.find(l => l.key === leagueKey);
    if (!league) return 0;
    return league.markets.filter(m => !m.isAlternate && selectedPlayerPropMarkets.includes(m.key)).length;
  };

  const isMoneylineSelected = selectedMarkets.includes('h2h');
  const playerPropCount = selectedPlayerPropMarkets.length;
  const atPlayerPropLimit = playerPropCount >= MAX_PLAYER_PROP_MARKETS;
  const canSelectPlayerProps = userTier === 'platinum';

  const handleSaveFilter = () => {
    setSaveError(null);

    if (!filterName.trim()) {
      setSaveError('Please enter a filter name');
      return;
    }

    const filter: SavedFilter = isEVMode
      ? {
        type: 'ev',
        name: filterName.trim(),
        selectedSports,
        selectedMarkets,
        selectedBookmakers,
        selectedGameStatus,
        minEV: minProfit,
        maxEV: maxProfit,
        minProbability,
        maxProbability,
      }
      : {
        type: 'arbitrage',
        name: filterName.trim(),
        selectedSports,
        selectedRegions,
        selectedMarkets,
        selectedPlayerPropMarkets,
        selectedBookmakers,
        selectedGameStatus,
        minProfit,
        maxProfit,
      };

    const success = saveFilter(filter);
    if (success) {
      setFilterName('');
      setShowSaveDialog(false);
      const filters = getSavedFiltersByType(isEVMode ? 'ev' : 'arbitrage');
      setSavedFilters(filters);
    } else {
      setSaveError('Failed to save filter. Please try again.');
    }
  };

  const handleLoadFilter = (filter: SavedFilter) => {
    if (filter.type === 'ev' && isEVMode) {
      const evFilter = filter as EVFilters;
      setSelectedSports(evFilter.selectedSports);
      if (evFilter.selectedMarkets) {
        setSelectedMarkets(evFilter.selectedMarkets);
      }
      setSelectedBookmakers(evFilter.selectedBookmakers);
      setSelectedGameStatus(evFilter.selectedGameStatus);
      setMinProfit(evFilter.minEV);
      setMaxProfit(evFilter.maxEV);
      setMinProbability(evFilter.minProbability);
      setMaxProbability(evFilter.maxProbability);
    } else if (filter.type === 'arbitrage' && !isEVMode) {
      const arbFilter = filter as ArbitrageFilters;
      setSelectedSports(arbFilter.selectedSports);
      _setSelectedRegions(arbFilter.selectedRegions);
      setSelectedMarkets(arbFilter.selectedMarkets);
      setSelectedPlayerPropMarkets(arbFilter.selectedPlayerPropMarkets || []);
      setSelectedBookmakers(arbFilter.selectedBookmakers);
      setSelectedGameStatus(arbFilter.selectedGameStatus);
      setMinProfit(arbFilter.minProfit);
      setMaxProfit(arbFilter.maxProfit);
    }
  };

  const handleDeleteFilter = (filterName: string) => {
    const success = deleteFilter(filterName, isEVMode ? 'ev' : 'arbitrage');
    if (success) {
      const filters = getSavedFiltersByType(isEVMode ? 'ev' : 'arbitrage');
      setSavedFilters(filters);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="filters-panel-overlay" onClick={onCancel}></div>

      {/* Panel */}
      <div className="filters-panel">
        {/* Header */}
        <div className="filters-panel-header">
          <h2>Filters</h2>
          <button className="filters-panel-close" onClick={onCancel}>×</button>
        </div>

        <div className="filters-panel-content">
          {/* Left Sidebar - Categories */}
          <div className="filters-panel-categories">
            {categories.map((category) => (
              <button
                key={category.key}
                className={`filters-category-btn ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                <span className="filters-category-icon">{category.icon}</span>
                <span className="filters-category-label">{category.label}</span>
              </button>
            ))}
          </div>

          {/* Right Content - Category Details */}
          <div className="filters-panel-details">
            {/* Saved Filters Category */}
            {activeCategory === 'saved' && (
              <div className="filters-category-content">
                <h3>Saved Filter Presets</h3>
                <p className="filters-category-description">
                  Save your current filter configuration or load a saved preset
                </p>

                {/* Save Current Filters Button */}
                <button
                  className="filters-save-preset-btn"
                  onClick={() => setShowSaveDialog(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                  Save Current Filters
                </button>

                {/* Save Dialog */}
                {showSaveDialog && (
                  <div className="filters-save-dialog">
                    <h4>Save Filter Preset</h4>
                    <input
                      type="text"
                      className="filters-save-input"
                      placeholder="Enter preset name..."
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveFilter();
                        } else if (e.key === 'Escape') {
                          setShowSaveDialog(false);
                          setFilterName('');
                          setSaveError(null);
                        }
                      }}
                      autoFocus
                    />
                    {saveError && <div className="filters-save-error">{saveError}</div>}
                    <div className="filters-save-actions">
                      <button
                        className="filters-save-cancel-btn"
                        onClick={() => {
                          setShowSaveDialog(false);
                          setFilterName('');
                          setSaveError(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button className="filters-save-confirm-btn" onClick={handleSaveFilter}>
                        Save Preset
                      </button>
                    </div>
                  </div>
                )}

                {/* Saved Filters List */}
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                    Your Saved Presets {savedFilters.length > 0 && `(${savedFilters.length})`}
                  </h4>

                  {savedFilters.length === 0 ? (
                    <div className="filters-empty-state">
                      <div className="filters-empty-icon">⭐</div>
                      <div className="filters-empty-text">No saved filters yet</div>
                      <div className="filters-empty-subtext">Save your current configuration to quickly access it later</div>
                    </div>
                  ) : (
                    <div className="filters-saved-list">
                      {savedFilters.map((filter) => (
                        <div key={filter.name} className="filters-saved-item">
                          <button
                            className="filters-saved-load-btn"
                            onClick={() => handleLoadFilter(filter)}
                            title="Load this preset"
                          >
                            <span className="filters-saved-icon">⭐</span>
                            <span className="filters-saved-name">{filter.name}</span>
                          </button>
                          <button
                            className="filters-saved-delete-btn"
                            onClick={() => handleDeleteFilter(filter.name)}
                            title="Delete this preset"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sportsbook Category */}
            {activeCategory === 'sportsbook' && (
              <div className="filters-category-content">
                <h3>Select Sportsbooks</h3>
                <p className="filters-category-description">
                  Choose specific sportsbooks to filter opportunities. Leave empty to include all available.
                </p>

                {selectedBookmakers.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      className="filters-clear-btn"
                      onClick={() => setSelectedBookmakers([])}
                    >
                      Clear All ({selectedBookmakers.length})
                    </button>
                  </div>
                )}

                {Object.entries(BOOKMAKERS_BY_REGION).map(([regionKey, bookmakers]) => {
                  const regionBookmakerKeys = bookmakers.map(b => b.key);
                  const allSelected = regionBookmakerKeys.every(key => selectedBookmakers.includes(key));
                  const someSelected = regionBookmakerKeys.some(key => selectedBookmakers.includes(key));

                  return (
                    <div key={regionKey} className="filters-bookmaker-region">
                      <div className="filters-bookmaker-region-header">
                        <h4 className="filters-bookmaker-region-title">
                          {REGION_LABELS[regionKey] || regionKey.toUpperCase()}
                        </h4>
                        <button
                          className="filters-select-all-btn"
                          onClick={() => handleSelectAllInRegion(regionKey)}
                        >
                          {allSelected ? 'Deselect All' : someSelected ? 'Select All' : 'Select All'}
                        </button>
                      </div>
                      <div className="filters-bookmaker-grid">
                        {bookmakers.map((bookmaker) => {
                          const isSelected = selectedBookmakers.includes(bookmaker.key);
                          return (
                            <div
                              key={bookmaker.key}
                              className={`filters-bookmaker-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleBookmakerToggle(bookmaker.key)}
                            >
                              <div className="filters-bookmaker-logo">
                                <BookmakerLogo
                                  bookmakerKey={bookmaker.key}
                                  bookmakerTitle={bookmaker.title}
                                />
                              </div>
                              <div className="filters-bookmaker-name">{bookmaker.title}</div>
                              {isSelected && (
                                <div className="filters-bookmaker-check">✓</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Percentage Category */}
            {activeCategory === 'percentage' && (
              <div className="filters-category-content">
                <h3>{isEVMode ? 'EV Range' : 'Profit Range'}</h3>
                <p className="filters-category-description">
                  Filter opportunities by {isEVMode ? 'expected value' : 'profit'} percentage
                </p>

                <div className="filters-range-group">
                  <div className="filters-range-item">
                    <label>Minimum: {minProfit.toFixed(1)}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={minProfit}
                      onChange={(e) => setMinProfit(parseFloat(e.target.value))}
                      className="filters-range-slider"
                    />
                  </div>

                  <div className="filters-range-item">
                    <label>Maximum: {maxProfit.toFixed(1)}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={maxProfit}
                      onChange={(e) => setMaxProfit(parseFloat(e.target.value))}
                      className="filters-range-slider"
                    />
                  </div>
                </div>

                <div className="filters-range-display">
                  <div className="filters-range-display-box">
                    <span className="filters-range-display-label">{isEVMode ? 'EV' : 'Profit'} Range</span>
                    <span className="filters-range-display-value">
                      {minProfit.toFixed(1)}% - {maxProfit.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {isEVMode && (
                  <>
                    <h3 style={{ marginTop: '32px' }}>Probability Range</h3>
                    <p className="filters-category-description">
                      Filter by consensus win probability (lower probability = underdog)
                    </p>

                    <div className="filters-range-group">
                      <div className="filters-range-item">
                        <label>Minimum: {minProbability.toFixed(1)}%</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={minProbability}
                          onChange={(e) => setMinProbability(parseFloat(e.target.value))}
                          className="filters-range-slider"
                        />
                      </div>

                      <div className="filters-range-item">
                        <label>Maximum: {maxProbability.toFixed(1)}%</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={maxProbability}
                          onChange={(e) => setMaxProbability(parseFloat(e.target.value))}
                          className="filters-range-slider"
                        />
                      </div>
                    </div>

                    <div className="filters-range-display">
                      <div className="filters-range-display-box">
                        <span className="filters-range-display-label">Probability Range</span>
                        <span className="filters-range-display-value">
                          {minProbability.toFixed(1)}% - {maxProbability.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Sport Category */}
            {activeCategory === 'sport' && (
              <div className="filters-category-content">
                <h3>Select Sports</h3>
                <p className="filters-category-description">
                  Choose which sports to include in your search
                </p>

                <div className="filters-checkbox-grid">
                  <label className="filters-checkbox-card">
                    <input
                      type="checkbox"
                      checked={selectedSports.includes('upcoming')}
                      onChange={() => handleSportToggle('upcoming')}
                    />
                    <span className="filters-checkbox-label">All Upcoming Games</span>
                    <span className="filters-checkbox-check">✓</span>
                  </label>

                  {sports.map((sport) => (
                    <label key={sport.key} className="filters-checkbox-card">
                      <input
                        type="checkbox"
                        checked={selectedSports.includes(sport.key)}
                        onChange={() => handleSportToggle(sport.key)}
                      />
                      <span className="filters-checkbox-label">{sport.title}</span>
                      <span className="filters-checkbox-check">✓</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Market Category */}
            {activeCategory === 'market' && (
              <div className="filters-category-content">
                {isEVMode ? (
                  // EV Mode - show market selection
                  <>
                    <h3>Game Markets</h3>
                    <p className="filters-category-description">
                      Choose which betting markets to search
                    </p>

                    <div className="filters-checkbox-grid">
                      {MARKETS.map((market) => (
                        <label key={market.key} className="filters-checkbox-card">
                          <input
                            type="checkbox"
                            checked={selectedMarkets.includes(market.key)}
                            onChange={() => handleMarketToggle(market.key)}
                          />
                          <span className="filters-checkbox-label">{market.name}</span>
                          <span className="filters-checkbox-check">✓</span>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  // Arbitrage Mode - allow moneyline and player prop selection
                  <>
                    <h3>Markets</h3>
                    <p className="filters-category-description">
                      Choose moneyline and player prop markets to include in your search
                    </p>

                    <div className="filters-checkbox-grid">
                      <label className="filters-checkbox-card">
                        <input
                          type="checkbox"
                          checked={isMoneylineSelected}
                          onChange={() => handleMarketToggle('h2h')}
                          disabled={isMoneylineSelected && playerPropCount === 0}
                        />
                        <span className="filters-checkbox-label">Moneyline (H2H)</span>
                        <span className="filters-checkbox-check">v</span>
                      </label>
                    </div>

                    <div className="filters-player-props-section">
                      <h3>Player Props</h3>
                      <p className="filters-category-description">
                        Select up to {MAX_PLAYER_PROP_MARKETS} player prop markets across leagues
                      </p>

                      {canSelectPlayerProps ? (
                        <>
                          <div className="filters-player-props-counter">
                            <span>Selected markets</span>
                            <span className={`counter-badge ${atPlayerPropLimit ? 'at-limit' : ''}`}>
                              {playerPropCount}/{MAX_PLAYER_PROP_MARKETS}
                            </span>
                          </div>

                          <div className="filters-player-props-leagues">
                            {PLAYER_PROP_LEAGUES.map((league) => {
                              const isExpanded = expandedLeagues.includes(league.key);
                              const selectedCount = getSelectedCountForLeague(league.key);
                              const standardMarkets = league.markets.filter(m => !m.isAlternate);

                              return (
                                <div key={league.key} className="filters-league-accordion">
                                  <button
                                    className={`filters-league-header ${isExpanded ? 'expanded' : ''}`}
                                    onClick={() => handleLeagueToggle(league.key)}
                                  >
                                    <div className="filters-league-header-left">
                                      <span className={`filters-league-chevron ${isExpanded ? 'expanded' : ''}`}>
                                        {'>'}
                                      </span>
                                      <span className="filters-league-name">{league.name}</span>
                                      {selectedCount > 0 && (
                                        <span className="filters-league-selected-badge">{selectedCount}</span>
                                      )}
                                    </div>
                                    <span className="filters-league-market-count">
                                      {standardMarkets.length} markets
                                    </span>
                                  </button>
                                  {isExpanded && (
                                    <div className="filters-league-content">
                                      <div className="filters-league-actions">
                                        <button
                                          className="filters-select-all-btn"
                                          onClick={() => handleSelectAllMarketsInLeague(league.key)}
                                        >
                                          {selectedCount === standardMarkets.length ? 'Clear' : 'Select all'}
                                        </button>
                                      </div>
                                      <div className="filters-market-section">
                                        <div className="filters-market-grid">
                                          {standardMarkets.map((market) => {
                                            const isSelected = selectedPlayerPropMarkets.includes(market.key);
                                            const isDisabled = !isSelected && atPlayerPropLimit;

                                            return (
                                              <label
                                                key={market.key}
                                                className={`filters-market-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={isSelected}
                                                  onChange={() => handlePlayerPropMarketToggle(market.key)}
                                                  disabled={isDisabled}
                                                />
                                                <span className="filters-market-name">{market.name}</span>
                                                <span className="filters-market-check">v</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <div className="filters-tier-lock-container">
                          <div className="filters-tier-lock-icon">dY"'</div>
                          <h4>Upgrade to Platinum</h4>
                          <p>Player prop markets are available for Platinum members.</p>
                          <a href="/account/billing" className="filters-upgrade-btn">
                            Upgrade Now
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Date Range Category */}
            {activeCategory === 'date_range' && (
              <div className="filters-category-content">
                <h3>Game Status</h3>
                <p className="filters-category-description">
                  Filter by game timing
                </p>

                <div className="filters-checkbox-grid">
                  <label className={`filters-checkbox-card ${userTier === 'free' ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedGameStatus.includes('live')}
                      onChange={() => {
                        if (userTier !== 'free') {
                          handleGameStatusToggle('live');
                        }
                      }}
                      disabled={userTier === 'free'}
                    />
                    <span className="filters-checkbox-label">Live Games</span>
                    {userTier === 'free' ? (
                      <span className="filters-upgrade-badge">PRO</span>
                    ) : (
                      <span className="filters-checkbox-check">✓</span>
                    )}
                  </label>

                  {userTier === 'free' && (
                    <div className="filters-upgrade-cta">
                      <p>Live arbitrage is available on Gold & Platinum plans.</p>
                      <a href="/account/billing" className="filters-upgrade-link">
                        Upgrade to Gold
                      </a>
                    </div>
                  )}

                  <label className="filters-checkbox-card">
                    <input
                      type="checkbox"
                      checked={selectedGameStatus.includes('upcoming')}
                      onChange={() => handleGameStatusToggle('upcoming')}
                    />
                    <span className="filters-checkbox-label">Upcoming Games</span>
                    <span className="filters-checkbox-check">✓</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with credit cost and save button */}
        <div className="filters-panel-footer">
          <div className="filters-cost-breakdown">
            {/* Cost display removed */}
          </div>
          <button className="filters-save-btn" onClick={onApply}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: '18px', height: '18px', marginRight: '8px' }}
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Save Filters
          </button>
        </div>
      </div>
    </>
  );
}
